/**
 * TelemetryPanel — displays current simulation telemetry with proper units.
 * All values come from the physics engine telemetry; radians are converted
 * to degrees only for presentation.
 */

import type { TelemetryPoint, LandingResult } from "@/lib/physics";
import { Panel, Stat, StatGrid, SectionTitle } from "@/components/ui";
import {
  formatSpeed,
  formatAltitude,
  formatFuel,
  formatThrottle,
  formatTime,
  formatDistance,
  formatAcceleration,
} from "@/lib/format";

interface TelemetryPanelProps {
  reading: TelemetryPoint | null;
  landing: LandingResult | null;
  maxFuel?: number;
}

function radToDeg(rad: number): number {
  return (rad * 180) / Math.PI;
}

export function TelemetryPanel({ reading, landing, maxFuel = 5000 }: TelemetryPanelProps) {
  if (!reading) {
    return (
      <Panel title="Live Telemetry" padded={false}>
        <div className="p-3 h-[320px] flex flex-col items-center justify-center text-muted">
          <p>Waiting for simulation data&hellip;</p>
        </div>
      </Panel>
    );
  }

  const mass = reading.mass;
  const fuelPct = maxFuel > 0 ? (reading.fuel / maxFuel) * 100 : 0;

  const touchdownStatus = landing
    ? landing.landingSuccess
      ? { label: "LANDED", variant: "safe" as const }
      : { label: "IMPACT", variant: "danger" as const }
    : { label: "IN FLIGHT", variant: "muted" as const };

  return (
    <Panel title="Live Telemetry" padded={false}>
      <div className="p-3 space-y-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[0.625rem] text-muted uppercase tracking-wider">Flight Phase</span>
          <span
            className={`px-2 py-0.5 rounded text-[0.625rem] font-medium ${
              touchdownStatus.variant === "safe"
                ? "bg-ok-500/10 text-ok-500 border border-ok-500/30"
                : touchdownStatus.variant === "danger"
                ? "bg-danger-500/10 text-danger-400 border border-danger-500/30"
                : "bg-surface text-dim border border-border"
            }`}
          >
            {touchdownStatus.label}
          </span>
        </div>

        <StatGrid cols={4}>
          <Stat label="MET" value={formatTime(reading.t)} unit="s" accent="cyan" />
          <Stat label="Altitude" value={formatAltitude(reading.altitude)} accent="cyan" />
          <Stat label="Speed" value={formatSpeed(reading.speed)} accent="blue" />
          <Stat label="Fuel" value={formatFuel(reading.fuel)} accent="amber" />
          <Stat label="VX" value={formatSpeed(reading.vx)} accent="blue" />
          <Stat label="VZ" value={formatSpeed(reading.vz)} accent="blue" />
          <Stat label="Mass" value={formatFuel(mass)} accent="amber" />
          <Stat label="Fuel %" value={formatPercent(fuelPct)} accent="amber" />
          <Stat label="Throttle" value={formatThrottle(reading.throttle)} accent="amber" />
          <Stat label="Gimbal" value={`${reading.gimbal.toFixed(2)} rad`} accent="blue" />
          <Stat label="Pitch" value={`${radToDeg(reading.theta).toFixed(1)}°`} accent="cyan" />
          <Stat label="Pitch Rate" value={`${reading.omega.toFixed(2)} rad/s`} accent="blue" />
          <Stat label="Accel X" value={formatAcceleration(reading.ax)} accent="blue" />
          <Stat label="Accel Z" value={formatAcceleration(reading.az)} accent="blue" />
          <Stat label="Accel Mag" value={formatAcceleration(reading.accelMagnitude)} accent="blue" />
          <Stat label="Throttle Eff" value={formatThrottle(reading.effectiveThrottle)} accent="amber" />
        </StatGrid>

        {landing && (
          <div className="border-t border-border pt-3">
            <SectionTitle>Touchdown Result</SectionTitle>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat
                label="Touchdown Speed"
                value={formatSpeed(landing.touchdownSpeed)}
                accent={landing.landingSuccess ? "cyan" : "amber"}
              />
              <Stat
                label="Touchdown VX"
                value={formatSpeed(landing.touchdownVx)}
                accent="blue"
              />
              <Stat
                label="Touchdown VZ"
                value={formatSpeed(landing.touchdownVz)}
                accent="blue"
              />
              <Stat
                label="Horizontal Error"
                value={formatDistance(landing.horizontalError)}
                accent={landing.horizontalError <= 10 ? "cyan" : "amber"}
              />
            </div>
          </div>
        )}
      </div>
    </Panel>
  );
}

function formatPercent(value: number): string {
  return `${value.toFixed(0)}%`;
}
